import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { Col, Row, Container } from "react-bootstrap";
import { customerServices } from "@/services/customerServices";
import { healerServices } from "@/services/healerServices";
import Layout from "@/components/layout";
import Faq from "@/components/faq";
import Testimonial from "@/components/customer/testimonials";
import styles from "@/styles/Services.module.css";
import Image from "next/image";
import Head from "next/head";

const Services = (props) => {
  const [testimonials, setTestimonial] = useState(props?.testimonials ?? []);
  const [servicesMeta, setServicesMeta] = useState(props?.servicesMeta ?? {});
  const [showSideButton, setShowSideButton] = useState(false);
  const firstSectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!firstSectionRef.current) return;

      const sectionTop = firstSectionRef.current.getBoundingClientRect().bottom;

      setShowSideButton(sectionTop < 0);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Head>
        <title>{servicesMeta?.meta?.og_title}</title>
        <meta name="title" content={servicesMeta?.meta?.meta_title}></meta>
        <meta
          name="description"
          content={servicesMeta?.meta?.meta_description}
        ></meta>
        <meta
          name="keywords"
          content={servicesMeta?.meta?.meta_keywords}
        ></meta>
        <meta property="og:type" content="website"></meta>
        <meta property="og:title" content={servicesMeta?.meta?.og_title}></meta>
        <meta property="og:image" content={servicesMeta?.meta?.og_image}></meta>
        <link rel="canonical" href="https://www.zenkify.com/services"></link>
        <meta
          property="og:description"
          content={servicesMeta?.meta?.meta_description}
        ></meta>
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={servicesMeta?.meta?.meta_title} />
        <meta
          name="twitter:description"
          content={servicesMeta?.meta?.meta_description}
        />
        <meta name="twitter:image" content={servicesMeta?.meta?.og_image} />
        <meta property="og:url" content="https://www.zenkify.com/services" />
        <meta property="og:site_name" content="Zenkify" />
        <meta property="og:image:alt" content="zenkify services" />
        <meta property="og:image" content={servicesMeta?.meta?.og_image}></meta>
      </Head>
      <section
        className={`${styles["section-banner"]} "banner-page"`}
        style={{ background: "url(/images/our-services-banner.png)" }}
        ref={firstSectionRef}
      >
        <Container>
          <Row>
            <Col lg={8} className="m-auto">
              <div className={styles["inner-content"]}>
                <h1 className={styles["banner-title"]}> our Service</h1>
                <p>
                  At Zenkify, we believe in the power of energy healing and its
                  ability to restore balance and harmony within the body, mind,
                  and spirit. Our Reiki Healing Service offers a holistic
                  approach to well-being, aiming to support your overall health
                  and wellness.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section
        className={`${styles["reiki-healing"]} ${styles["content-grid"]}`}
      >
        <Image
          width={100}
          height={101}
          src="/images/flower-2.svg"
          className={styles["flower-image"]}
          alt="flower"
        />
        <Container>
          <Row>
            <Col lg={4}>
              <div className={styles["image"]}>
                <Image
                  width={400}
                  height={375}
                  src="/images/reiki-healing-service.svg"
                  alt="Reiki Healing Service"
                />
              </div>
            </Col>
            <Col lg={7} className="ms-auto align-self-center">
              <div className={styles["inner-content"]}>
                <h2>Reiki Healing</h2>
                <p>
                  Reiki is a holistic healing practice that originated in Japan
                  in the early 20th century. It is based on the concept of
                  channeling universal life energy to promote balance,
                  relaxation, and healing. Reiki is a form of energy healing
                  that focuses on the flow of life force energy, also known as
                  &quot;ki&quot; or &quot;qi&quot;. Practitioners believe that
                  disruptions or imbalances in this energy can lead to physical,
                  emotional, or spiritual ailments. Reiki aims to restore
                  harmony and facilitate the body&apos;s natural healing
                  abilities.
                </p>
                <Link href={"/services/reiki-healing"} className="theme-btn">
                  Read More <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section
        className={`${styles["content-grid"]} ${styles["theta-healing"]}`}
        style={{ background: "url(/images/theta-healing-banner.jpg)" }}
      >
        <Container>
          <Row>
            <Col md={4} className="d-md-none mb-3">
              <div className={styles["image"]}>
                <Image
                  width={581}
                  height={500}
                  src="/images/about-theta-healing.png"
                  alt="About Theta Healing"
                />
              </div>
            </Col>
            <Col lg={7} md={10} className="me-auto align-self-center">
              <div className={styles["inner-content"]}>
                <h3>Theta Healing</h3>
                <p>
                  Theta Healing is a holistic healing technique that combines
                  aspects of meditation, energy healing, and belief work. It was
                  developed by Vianna Stibal in the 1990s and is based on the
                  premise that our beliefs and thoughts have a profound impact
                  on our physical, emotional, and spiritual well-being. Theta
                  Healing derives its name from the theta brainwave state, which
                  is associated with deep relaxation and heightened intuition.
                  During a Theta Healing session, the practitioner and recipient
                  enter a meditative state where the brainwave activity slows
                  down to the theta frequency. This relaxed state allows access
                  to the subconscious mind, where deep-seated beliefs and
                  patterns can be explored and transformed.
                </p>
                <Link href={"/services/theta-healing"} className="theme-btn">
                  Read More <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section
        className={`${styles["content-grid"]} ${styles["healing-touch"]}`}
      >
        <Image
          width={150}
          height={101}
          src="/images/flower-shap-3.svg"
          className={styles["flower-top"]}
          alt="flower-shap"
        />
        <Container>
          <Row>
            <Col lg={5}>
              <div className={styles["image"]}>
                <Image
                  width={400}
                  height={364}
                  src="/images/healing-touch-service.svg"
                  alt="Healing Touch Service"
                />
              </div>
            </Col>
            <Col lg={7} className="ms-auto align-self-center">
              <div className={styles["inner-content"]}>
                <h3>Healing Touch</h3>
                <p>
                  Healing Touch is an energy-based healing modality that
                  involves using the hands to promote physical, emotional,
                  mental, and spiritual well-being. Healing Touch is based on
                  the principle that the human body has an energy system that
                  can be influenced for healing purposes. Practitioners use
                  their hands to assess and manipulate the energy field
                  surrounding the body, working with the energy centers
                  (chakras) and energy pathways (meridians) to restore balance
                  and promote the body&apos;s natural healing abilities.
                </p>
                <Link href={"/services/healing-touch"} className="theme-btn">
                  Read More <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
        <Image
          width={79}
          height={130}
          src="/images/icon/flower-2.svg"
          className={styles["flower-bottom"]}
          alt="flower-shap"
        />
      </section>
      <section
        className={`${styles["reiki-healing"]} ${styles["content-grid"]}`}
      >
        {/* <Image
          width={100}
          height={101}
          src="/images/flower-2.svg"
          className={styles["flower-image"]}
          alt="flower"
        /> */}
        <Container>
          <Row>
            <Col lg={7} className="ms-auto align-self-center">
              <div className={styles["inner-content"]}>
                <h2>Chakra Balancing</h2>
                <p>
                  Chakra balancing is a crucial technique in energy healing,
                  focusing on the alignment and activation of the body&apos;s
                  seven main chakras. This method is designed to improve energy
                  flow and restore balance across physical, emotional, and
                  spiritual planes, promoting overall wellness and vitality.
                  It&apos;s particularly beneficial for enhancing personal
                  development and emotional clarity.
                </p>
                {/* <Link href={"/services/reiki-healing"} className="theme-btn">Read More <i className="ri-arrow-right-line"></i></Link> */}
              </div>
            </Col>
            <Col lg={4}>
              <div className={styles["image"]}>
                <Image
                  width={400}
                  height={375}
                  src="/images/chakra-balancing.jpg"
                  alt="Chakra Balancing"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section
        className={`${styles["reiki-healing"]} ${styles["content-grid"]}`}
        style={{ paddingTop: "0px" }}
      >
        {/* <Image
          width={100}
          height={101}
          src="/images/flower-2.svg"
          className={styles["flower-image"]}
          alt="flower"
        /> */}
        <Container>
          <Row>
            <Col lg={4}>
              <div className={styles["image"]}>
                <Image
                  width={400}
                  height={375}
                  src="/images/crystal-healing.jpg"
                  alt="Crystal Healing"
                />
              </div>
            </Col>
            <Col lg={7} className="ms-auto align-self-center">
              <div className={styles["inner-content"]}>
                <h2>Crystal Healing</h2>
                <p>
                  Crystal Healing leverages the intrinsic properties of crystals
                  to stabilize and rejuvenate the body’s energy systems.
                  Practitioners strategically place specific stones on or around
                  the body to clear blockages, support energy flow, and catalyze
                  physical and emotional healing processes. This therapy is
                  well-regarded for stress reduction, energetic balance, and its
                  support in holistic wellness practices.
                </p>
                {/* <Link href={"/services/reiki-healing"} className="theme-btn">Read More <i className="ri-arrow-right-line"></i></Link> */}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section
        className={`${styles["reiki-healing"]} ${styles["content-grid"]}`}
        style={{ paddingTop: "0px" }}
      >
        {/* <Image
          width={100}
          height={101}
          src="/images/flower-2.svg"
          className={styles["flower-image"]}
          alt="flower"
        /> */}
        <Container>
          <Row>
            <Col lg={7} className="ms-auto align-self-center">
              <div className={styles["inner-content"]}>
                <h2>Pranic Healing</h2>
                <p>
                  Pranic Healing, an esteemed no-touch healing practice,
                  utilizes prana (life force) to balance the body&apos;s energy
                  fields. It enhances the body&apos;s capability to heal itself
                  by removing energy blockages and increasing vitality. Pranic
                  Healing is highly effective in treating physical and
                  psychological ailments, boosting immunity, and maintaining
                  good health and energy levels.
                </p>
                {/* <Link href={"/services/reiki-healing"} className="theme-btn">Read More <i className="ri-arrow-right-line"></i></Link> */}
              </div>
            </Col>
            <Col lg={4}>
              <div className={styles["image"]}>
                <Image
                  width={400}
                  height={375}
                  src="/images/pranic-healing.jpg"
                  alt="Reiki Healing Service"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section
        className={`${styles["reiki-healing"]} ${styles["content-grid"]}`}
        style={{ paddingTop: "0px" }}
      >
        {/* <Image
          width={100}
          height={101}
          src="/images/flower-2.svg"
          className={styles["flower-image"]}
          alt="flower"
        /> */}
        <Container>
          <Row>
            <Col lg={4}>
              <div className={styles["image"]}>
                <Image
                  width={400}
                  height={375}
                  src="/images/polarity-therapyy.png"
                  alt="Reiki Healing Service"
                />
              </div>
            </Col>
            <Col lg={7} className="ms-auto align-self-center">
              <div className={styles["inner-content"]}>
                <h2>Polarity Therapy</h2>
                <p>
                  Polarity Therapy combines bodywork, nutritional guidance, and
                  self-awareness exercises to foster balance within the body’s
                  energy systems. It addresses underlying stress patterns,
                  supports detoxification, and encourages healing by aligning
                  body structures through gentle manipulations. This holistic
                  approach is particularly valuable for chronic stress relief
                  and maintaining overall health equilibrium.
                </p>
                {/* <Link href={"/services/reiki-healing"} className="theme-btn">Read More <i className="ri-arrow-right-line"></i></Link> */}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section
        className={`${styles["reiki-healing"]} ${styles["content-grid"]}`}
        style={{ paddingTop: "0px" }}
      >
        {/* <Image
          width={100}
          height={101}
          src="/images/flower-2.svg"
          className={styles["flower-image"]}
          alt="flower"
        /> */}
        <Container>
          <Row>
            <Col lg={7} className="ms-auto align-self-center">
              <div className={styles["inner-content"]}>
                <h2>Shamanic Healing</h2>
                <p>
                  Shamanic Healing is one of the oldest holistic healing
                  practices, utilizing rituals, ceremonies, and natural
                  medicines to restore balance and peace to the individual’s
                  body, mind, and spirit. It often involves journeying to other
                  realms to retrieve energies and guidance, which can offer
                  profound insights and promote healing from life traumas and
                  spiritual maladies.
                </p>
                {/* <Link href={"/services/reiki-healing"} className="theme-btn">Read More <i className="ri-arrow-right-line"></i></Link> */}
              </div>
            </Col>
            <Col lg={4}>
              <div className={styles["image"]}>
                <Image
                  width={400}
                  height={375}
                  src="/images/shamanic-healing.jpg"
                  alt="Reiki Healing Service"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section
        className={`${styles["reiki-healing"]} ${styles["content-grid"]}`}
        style={{ paddingTop: "0px" }}
      >
        {/* <Image
          width={100}
          height={101}
          src="/images/flower-2.svg"
          className={styles["flower-image"]}
          alt="flower"
        /> */}
        <Container>
          <Row>
            <Col lg={4}>
              <div className={styles["image"]}>
                <Image
                  width={400}
                  height={375}
                  src="/images/reconnective-healing.jpg"
                  alt="Reiki Healing Service"
                />
              </div>
            </Col>
            <Col lg={7} className="ms-auto align-self-center">
              <div className={styles["inner-content"]}>
                <h2>Reconnective Healing</h2>
                <p>
                  Reconnective Healing is recognized for accessing new,
                  comprehensive frequencies of healing energy that can transcend
                  traditional energy healing techniques. It enables profound
                  healing not only on a physical level but also expands to
                  emotional and spiritual healing, supporting overall wellness
                  and life progress.
                </p>
                {/* <Link href={"/services/reiki-healing"} className="theme-btn">Read More <i className="ri-arrow-right-line"></i></Link> */}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section
        className={`${styles["reiki-healing"]} ${styles["content-grid"]}`}
        style={{ paddingTop: "0px" }}
      >
        {/* <Image
          width={100}
          height={101}
          src="/images/flower-2.svg"
          className={styles["flower-image"]}
          alt="flower"
        /> */}
        <Container>
          <Row>
            <Col lg={7} className="ms-auto align-self-center">
              <div className={styles["inner-content"]}>
                <h2>Quantum Healing</h2>
                <p>
                  Quantum Healing integrates principles of quantum physics with
                  healing to address issues at a molecular level. This
                  innovative approach encourages profound physical healing and
                  emotional release by tapping into the body&apos;s potential to
                  heal itself. The practice often includes advanced meditation
                  techniques and focused intention, making it effective for
                  transforming energy patterns and promoting health.
                </p>
                {/* <Link href={"/services/reiki-healing"} className="theme-btn">Read More <i className="ri-arrow-right-line"></i></Link> */}
              </div>
            </Col>
            <Col lg={4}>
              <div className={styles["image"]}>
                <Image
                  width={400}
                  height={375}
                  src="/images/quantum-therepyy.png"
                  alt="Reiki Healing Service"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section
        className={`${styles["reiki-healing"]} ${styles["content-grid"]}`}
        style={{ paddingTop: "0px" }}
      >
        {/* <Image
          width={100}
          height={101}
          src="/images/flower-2.svg"
          className={styles["flower-image"]}
          alt="flower"
        /> */}
        <Container>
          <Row>
            <Col lg={4}>
              <div className={styles["image"]}>
                <Image
                  width={400}
                  height={375}
                  src="/images/bioenergy-therapyy.png"
                  alt="Reiki Healing Service"
                />
              </div>
            </Col>
            <Col lg={7} className="ms-auto align-self-center">
              <div className={styles["inner-content"]}>
                <h2>Bioenergy Therapy</h2>
                <p>
                  Bioenergy Therapy focuses on the manipulation and enhancement
                  of the body&apos;s energy field or biofield. This practice can
                  help alleviate a range of physical and mental health issues,
                  from chronic pain to anxiety and depression, by restoring the
                  disturbance in bioenergetic fields, often resulting in
                  improved energy levels and vitality.
                </p>
                {/* <Link href={"/services/reiki-healing"} className="theme-btn">Read More <i className="ri-arrow-right-line"></i></Link> */}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section
        className={`${styles["reiki-healing"]} ${styles["content-grid"]}`}
        style={{ paddingTop: "0px" }}
      >
        {/* <Image
          width={100}
          height={101}
          src="/images/flower-2.svg"
          className={styles["flower-image"]}
          alt="flower"
        /> */}
        <Container>
          <Row>
            <Col lg={7} className="ms-auto align-self-center">
              <div className={styles["inner-content"]}>
                <h2>Sound Healing</h2>
                <p>
                  Sound Healing is an effective therapeutic technique that uses
                  vibrational sounds to improve an individual&apos;s emotional
                  and physical health. Instruments like Tibetan singing bowls,
                  tuning forks, and gongs produce resonant vibrations that
                  promote relaxation, reduce stress, and increase mental
                  clarity. This modality is widely used in meditation practices
                  and stress management programs.
                </p>
                {/* <Link href={"/services/reiki-healing"} className="theme-btn">Read More <i className="ri-arrow-right-line"></i></Link> */}
              </div>
            </Col>
            <Col lg={4}>
              <div className={styles["image"]}>
                <Image
                  width={400}
                  height={375}
                  src="/images/sound-healing.png"
                  alt="Reiki Healing Service"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      {/* <Faq /> */}
      <Testimonial props={{ testimonials }} />
      <Link
        id="floatingBook"
        className="floating-book"
        aria-label="Book a FREE call now"
        href="/healers"
        style={{ opacity: showSideButton ? 1 : 0 }}
      >
        Book a FREE call now
      </Link>
    </>
  );
};

export default Services;

Services.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

// ssr
export async function getServerSideProps() {
  const fetchTestimonials = async () => {
    const res = await customerServices.getTestimonials();
    if (res.status) {
      return res.data;
    } else {
      toast.error(res.message, {
        autoClose: 5000,
      });
    }
  };

  const fetchServicesMeta = async () => {
    try {
      const res = await healerServices.getMetaInformation("services");
      if (res.status) {
        return res.data;
      }
    } catch (error) {
      console.error("Error fetching slider data:", error);
    }
  };

  const testimonials = (await fetchTestimonials()) ?? {};
  const servicesMeta = (await fetchServicesMeta()) ?? {};

  // Pass data to the page via props
  return { props: { testimonials, servicesMeta } };
}
